import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { Box as _Box } from '@dtx-company/design-system/src'
import { DEFAULT_AVATAR_DARK } from '../../constants/profile'
import { DesktopContactBar } from './ContactBar'
import { FC, useEffect } from 'react'
import { FlowpageCaptionText } from '../util/FlowpageCaptionText'
import { FlowpageModals } from './FlowPageContent/modals'
import { FontFamilies } from '@dtx-company/true-common/src/constants/page'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { Links } from './FlowPageContent/subcomponents/Links'
import { MaskType, theme } from '@dtx-company/shared-components/src'
import { PROFILE_IMAGE_MASK_RADIUS } from '../../constants/customizeTheme'
import { PaddingProps } from 'styled-system'
import { PoweredByFooter } from './PoweredByFooter'
import { Spacer } from '@dtx-company/shared-components/src/components/atoms/Spacer/index'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import {
  PageType as TypedPage,
  UnauthPage,
  UnauthPageQueryLink
} from '@dtx-company/flow-codegen/src/page/typeUtils'
import { Verified } from '../../components/Verified/Verified'
import { WIDGET_INFO } from '../widgets/manifest'
import { WidgetType } from '../widgets/types/widgetType'
import { checkRemoveBrandingEnabled } from '../RemoveBranding/utils'
import { getBgOpacity, getCustomThemeValues } from '../../utils/theme'
import { getGoogleFontStyleUrlFor } from '../profile/Customize/GoogleFonts/utils'
import { getWidgetComponent } from '../widgets/getWidgetComponent'
import { parseLinks, sortLinks } from '../../utils/main'
import { removeNulls } from '@dtx-company/true-common/src/utils/objects'
import { usePageDisplayContext } from '../../context/page-display'
import { useRouter } from 'next/router'
import { useScrollToWidget } from './FlowPageContent/hooks'
import Head from 'next/head'
import NextImage, { ImageProps } from 'next/image'
import PublishBanner from './PublishBanner'
import styled, { ThemeProvider } from 'styled-components'
import { Stack } from '@dtx-company/design-system/src'

export interface FlowPageProps {
  page: TypedPage | UnauthPage
  isOwnPage?: boolean
  hideFooterBanner?: boolean
  flowpageContentContainerStyleProps?: PaddingProps
  sentFromProfilePage?: boolean
  backgroundImageProps?: {
    opacity?: number
    position?: string
  } & Omit<ImageProps, 'src'>
}

export const FlowpageContainer = styled(Box)`
  position: relative;
  width: 100%;
  flex-direction: column;
  max-width: 100vw;
  justify-content: space-between;
`

const NextProfileAvatar = styled(NextImage)<{ $borderRadius?: string }>`
  border-radius: ${props => props.$borderRadius};
  object-fit: contain;
`

const ProfileHeader: FC<{ page: TypedPage | UnauthPage }> = ({ page }) => (
  <Box mt="50px" mb="16px">
    <NextProfileAvatar
      width={140}
      height={140}
      priority
      $borderRadius={
        PROFILE_IMAGE_MASK_RADIUS[
          (page?.theme?.profileImageMaskType as MaskType) || MaskType.CIRCLE
        ]
      }
      src={page?.profileImage || DEFAULT_AVATAR_DARK}
      alt={`${page?.displayName}'s Avatar`}
    />
  </Box>
)

const FlowPageContent: FC<FlowPageProps> = ({
  page,
  isOwnPage,
  flowpageContentContainerStyleProps,
  backgroundImageProps
}) => {
  const { setUnauthTheme } = usePageDisplayContext()
  const userLinks = removeNulls(page.links || [])
  const linkObj = parseLinks(userLinks)
  const { contact, links } = linkObj
  const { style, primaryColor, contrastText, baseColor, baseContrastText } = getCustomThemeValues(
    page?.theme
  )
  const backgroundImgUrl = page?.theme?.backgroundImgUrl
  const fontFamily = page?.theme?.fontFamily ?? FontFamilies.Inter
  const userTheme = {
    ...theme,
    fontFamily: `'${fontFamily}', ${theme.fontFamily}`,
    colors: {
      ...theme.colors,
      flowpage: {
        style,
        primaryColor,
        contrastText,
        backgroundImgUrl,
        baseColor,
        baseContrastText
      }
    }
  }
  const [fullPageLinks, cardLinks] = links.reduce(
    (acc: UnauthPageQueryLink[][], link) => {
      if (link.provider === LinkProvider.WIDGET) {
        const widgetType = link.type as WidgetType
        if (!WIDGET_INFO[widgetType]?.disableFlowpagePreview) {
          if (WIDGET_INFO[widgetType]?.isFullPage) {
            acc[0].push(link)
          } else {
            acc[1].push(link)
          }
        }
      } else {
        acc[1].push(link)
      }
      return acc
    },
    [[], []]
  )
  const sortedCardLinks = sortLinks(cardLinks)

  const router = useRouter()
  const { preview: isPreview } = router.query

  useScrollToWidget()

  useEffect(() => {
    if (page.theme) {
      setUnauthTheme(page.theme)
    }
  }, [setUnauthTheme, page.theme])

  const hideFooter = checkRemoveBrandingEnabled(page?.slots)
  const showHeader = page?.profileImage || page?.theme?.profileImageMaskType !== MaskType.NO_IMAGE
  const fontRequestUrl = getGoogleFontStyleUrlFor({
    fontFamily,
    withFallback: true
  })
  const color = page?.theme?.fontColor ?? 'flowpage.contrastText'
  const titleAndDescriptionColor = page?.theme?.titleColor || color
  return (
    <ThemeProvider theme={userTheme}>
      {fontFamily !== FontFamilies.Inter && (
        <Head>
          <link href={fontRequestUrl} rel="stylesheet" />
        </Head>
      )}
      <FlowpageContainer
        bg="flowpage.style"
        minHeight={'100%'}
        {...flowpageContentContainerStyleProps}
      >
        {page?.theme?.backgroundImgUrl && (
          <_Box
            sx={{
              position: backgroundImageProps?.position ?? 'fixed',
              height: '100vh',
              width: '100vw',
              opacity: backgroundImageProps?.opacity ?? getBgOpacity(page.theme.style),
              objectFit: 'cover',
              backgroundImage: `url(${page.theme.backgroundImgUrl})`,
              backgroundPosition: 'center center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat'
            }}
          />
        )}
        {isPreview && <PublishBanner pageId={page.id} />}
        <Stack
          sx={theme => ({
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: theme.spacing(125),
            margin: '0 auto',
            padding: theme.spacing(0, 4, 4),
            mt: isPreview ? theme.spacing(8) : 0,
            boxSizing: 'border-box',
            position: 'relative'
          })}
        >
          {showHeader ? <ProfileHeader page={page} /> : <Spacer mb="32px" />}
          <Box alignItems="center">
            <Text
              as="h1"
              textAlign="center"
              color={titleAndDescriptionColor}
              variant="display/large"
            >
              {page.displayName}
            </Text>
            <Verified verified={page?.verified} />
          </Box>
          <Spacer mb="8px" />
          <FlowpageCaptionText
            color={titleAndDescriptionColor}
            variant="body/medium"
            textAlign="center"
          >
            {page.caption}
          </FlowpageCaptionText>
          <Spacer mb="16px" />
          {linkObj.contact && <DesktopContactBar contacts={contact[0]} preview={isOwnPage} />}
          <Links links={sortedCardLinks} />
          {fullPageLinks.map(link => {
            const { provider, type } = link
            const Component = getWidgetComponent(provider as LinkProvider, type as WidgetType)

            return Component && link.active && <Component key={link.id} link={link} />
          })}
        </Stack>
        <FlowpageModals />
        {!hideFooter && <PoweredByFooter preview={false} contrastText={contrastText} />}
      </FlowpageContainer>
    </ThemeProvider>
  )
}

export default FlowPageContent
